import userEndpoints from "./endpoints/user.endpoints";

export async function getSignedInUser() {
    return userEndpoints
        .getSignedInUser()
        .then((response) => {
            return response.data;
        })
        .catch((ex) => {
            return {};
        });
}

/** Called from the Okta SPA callback after tokens are stored; updates server LastLogin. */
export async function recordLastLogin() {
    return userEndpoints.recordLastLogin();
}

export async function getUserById(id) {
    return userEndpoints
        .getUserById(id)
        .then((response) => {
            return response.data;
        })
        .catch((ex) => {
            return {};
        });
}

//check if currently signed in user has an alien.
export async function checkIfAlienExists() {
    try {
        const response = await userEndpoints.checkIfAlienExists();
        return response.data;

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    console.error("[checkIfAlienExists] Unauthorized: Invalid or missing user credentials.");
                    return -1;
                    break;
                case 400:
                    console.error(`[getFullAlienData] Invalid request.`);
                    return -1;
                    break;
                default:
                    break;
            }
        } else {
            console.error('[checkIfAlienExists] Unknown error:', error);
            return -1;
        }
        return null;
    }
}

//GET list of users (fetches all pages)
export async function getAllUsers() {
    try {
        let allItems = [];
        let page = 1;
        const pageSize = 100;

        while (true) {
            const response = await userEndpoints.getAllUsers(page, pageSize);
            if (response.status === 200) {
                const data = response.data || {};
                const pageItems = data.items ?? data.Items ?? [];
                const totalCount = data.totalCount ?? data.TotalCount;
                allItems = allItems.concat(pageItems);
                const pageLen = pageItems.length;
                const haveTotal = typeof totalCount === "number" && !Number.isNaN(totalCount);
                if (
                    (haveTotal && allItems.length >= totalCount)
                    || pageLen < pageSize
                    || pageLen === 0
                ) {
                    return { ...data, items: allItems };
                }
                page++;
            } else {
                console.error("Error with [getAllUsers] GET: ", response);
                return null;
            }
        }

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    console.error("[getAllUsers] Unauthorized: Invalid or missing user credentials.");
                    return -1;
                    break;
                case 403:
                    console.error("[getAllUsers] Forbidden: Admin user list requires AdminUsers policy.");
                    return -2;
                    break;
                case 400:
                    console.error(`[getAllUsers] Invalid request.`);
                    return -1;
                    break;
                default:
                    console.error("[getAllUsers] HTTP error:", error.response.status, error.response.data);
                    break;
            }
        } else {
            console.error('[getAllUsers] Unknown error:', error);
            return -1;
        }
        return null;
    }
}

//POST Create new User.
export async function newUserCreation(newUserPayload) {
    try {
        const response = await userEndpoints.newUserCreation(newUserPayload);
        if (response.status >= 200 && response.status < 300) {
            return { success: true };
        } else {
            console.error("Error with [createNewUser] POST: ", response);
            return { success: false, error: `Unexpected status ${response.status}` };
        }

    } catch (err) {
        const data = err?.response?.data;
        if (data) {
            if (typeof data.error === "string") {
                return { success: false, error: data.error };
            }
            if (data.errors && typeof data.errors === "object") {
                const messages = Object.values(data.errors)
                    .flatMap((arr) => Array.isArray(arr) ? arr : [String(arr)])
                    .join(", ");
                return { success: false, error: messages };
            }
            if (typeof data === "string") {
                return { success: false, error: data };
            }
        }
        return { success: false, error: err?.message || "An unknown error occurred" };
    }
}

/**
 * Hard-deletes a user from SQL and Okta. Backend refuses if the user has linked
 * defendant data — those go through the Bail Dashboard's Delete Defendant flow.
 * Returns { success: true } on 200, or { success: false, error } on 4xx/network failure.
 */
export async function deleteUser(userId) {
    try {
        const response = await userEndpoints.deleteUser(userId);
        if (response?.status === 200) {
            return { success: true };
        }
        return { success: false, error: response?.data?.error || "Unexpected response" };
    } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.error || err?.message || "Unknown error";
        if (status === 400) {
            // Backend's BadRequest carries a clear message (e.g. "user has linked defendant data").
            return { success: false, error: message };
        }
        if (status === 401) {
            return { success: false, error: "Unauthorized — please sign in again." };
        }
        if (status === 403) {
            return { success: false, error: "You don't have permission to delete this user." };
        }
        console.error("[deleteUser] failed:", err);
        return { success: false, error: message };
    }
}

//POST Create new User.
export async function deactivateUser(userId, payload) {
    try {
        const response = await userEndpoints.deactivateUser(userId, payload);
        console.log('deactivateUser response', response)

        if (response.status === 200) { //no response is expected
            return `User ${userId} deactivated successfuly`;
        } else {
            console.error("Error with [deactivateUser] PUT: ", response);
        }

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    console.error("[deactivateUser] Unauthorized: Invalid or missing user credentials.");
                    return -1;
                    break;
                case 400:
                    console.error(`[deactivateUser] Invalid request.`);
                    return -1;
                    break;
                default:
                    break;
            }
        } else {
            console.error('[deactivateUser] Unknown error:', error);
            return -1;
        }
        return null;
    }
}



/**
 * Change password for the authenticated user
 * @param {Object} payload - { currentPassword, newPassword, confirmPassword }
 * @returns {Promise<Object>} - { success: boolean, message: string } or throws error
 */
export async function changePassword(payload) {
    try {
        const response = await userEndpoints.changePassword(payload);
        return response.data;
    } catch (error) {
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }
        throw new Error('Failed to change password. Please try again.');
    }
}

// PUT: editable user profile (name, username, email, phone; organization for SuperAdmin). Roles use updateUserRoles.
export async function updateUser(userId, payload) {
    try {
        const response = await userEndpoints.updateUser(userId, payload);
        if (response.status >= 200 && response.status < 300) {
            return { success: true, data: response.data };
        }
        return { success: false, error: `Unexpected status ${response.status}` };
    } catch (err) {
        const data = err?.response?.data;
        if (data) {
            if (typeof data.error === "string") {
                return { success: false, error: data.error };
            }
            if (data.errors && typeof data.errors === "object") {
                const messages = Object.values(data.errors)
                    .flatMap((arr) => Array.isArray(arr) ? arr : [String(arr)])
                    .join(", ");
                return { success: false, error: messages };
            }
            if (typeof data === "string") {
                return { success: false, error: data };
            }
        }
        return { success: false, error: err?.message || "Failed to update user" };
    }
}

export async function updateUserRoles(userId, roleIds, allRoles) {
    try {
        const normalizeRoleId = (roleId) => {
            const numericRoleId = Number(roleId);
            return Number.isFinite(numericRoleId) ? numericRoleId : null;
        };
        const getRoleId = (role) => normalizeRoleId(role?.roleId ?? role?.RoleId ?? role?.id ?? role?.Id);
        const getRoleName = (role) => role?.roleName ?? role?.RoleName ?? role?.name ?? role?.Name ?? '';

        const normalizedRoleIds = roleIds
            .map(normalizeRoleId)
            .filter(id => id !== null);
        const roles = normalizedRoleIds
            .map(id => allRoles.find(r => getRoleId(r) === normalizeRoleId(id)))
            .filter(Boolean)
            .map(r => ({ roleId: getRoleId(r), roleName: getRoleName(r) }));

        if (roles.length !== normalizedRoleIds.length) {
            return {
                success: false,
                error: 'Role catalog is still loading or missing role metadata. Please refresh and try again.',
            };
        }

        const response = await userEndpoints.updateUserRoles(userId, roles);
        if (response.status === 200) {
            return { success: true, data: response.data };
        }
        return { success: false, error: `Unexpected status ${response.status}` };
    } catch (err) {
        const msg = err.response?.data?.error
            || err.response?.data?.title
            || err.message
            || 'Failed to update user roles';
        return { success: false, error: msg };
    }
}

//GET list of user roles
export async function getAllUserRoles() {
    try {
        const response = await userEndpoints.getAllUserRoles();

        if (response.status === 200) {
            return response.data;
        } else {
            console.error("Error with [getAllUserRoles] GET: ", response);
        }

    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    console.error("[getAllUserRoles] Unauthorized: Invalid or missing user credentials.");
                    return -1;
                    break;
                case 400:
                    console.error(`[getAllUserRoles] Invalid request.`);
                    return -1;
                    break;
                default:
                    break;
            }
        } else {
            console.error('[getAllUserRoles] Unknown error:', error);
            return -1;
        }
        return null;
    }
}
